import React, { useState, useMemo } from "react";
import { CheckBoxInput } from "../common/Buttons";
import { Tooltip, Popover } from "antd";
import { IconExclamation, IconHistory, XIcon } from "../Layout/Icons";
import { AiOutlineExclamation } from "react-icons/ai";
import styled from "styled-components";
import {
  numberRound,
  prettyDateFormat,
  returnEuroString,
} from "../../helpers/Utils";
import TableUi from "../Layout/TableUI";
import ImumProductTagPopover from "../ImumToAkeneoScreen/ImumProductTagPopover";
import { BlockSquare } from "../common/StyledComponents";
import { IconImumClock } from "../Layout/Icons";
import dayjs from "dayjs";
import RetailNewPriceChange from "./RetailNewPriceChange";
import ExportFailures from "../common/ExportFailures";
import { useDispatch } from "react-redux";
import { putRetailProduct } from "../../redux/Actions/imum_action_retail";

// RRP caps automatic pricing - click to edit, Enter/blur saves, empty clears the cap.
const RrpCell = ({ id, rrp_price }) => {
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const hasRrp = rrp_price !== null && rrp_price !== undefined;

  const startEdit = () => {
    setValue(hasRrp ? String(rrp_price) : "");
    setEditing(true);
  };

  const save = async () => {
    const normalized = value.trim().replace(",", ".");
    if (normalized !== "" && (isNaN(Number(normalized)) || Number(normalized) < 0)) {
      setEditing(false);
      return;
    }
    const nextValue = normalized === "" ? null : normalized;
    setEditing(false);
    if (String(nextValue) === String(hasRrp ? rrp_price : null)) return;
    await dispatch(putRetailProduct({ id, rrp_price: nextValue }));
  };

  if (editing) {
    return (
      <input
        autoFocus
        style={{ width: 80 }}
        value={value}
        placeholder="RRP"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }

  return (
    <Tooltip title="Click to edit RRP">
      <nobr style={{ cursor: "pointer" }} onClick={(e) => {
          e.stopPropagation();
          startEdit();
        }}>
        {hasRrp ? returnEuroString(rrp_price) : "-"}
      </nobr>
    </Tooltip>
  );
};

const RetailProductRow = ({
  active,
  setActive,
  id,
  product_code,
  product_name,
  imum_category,
  brand,
  ean_code,
  mpn,
  updated_at,
  checked,
  setChecked,
  lastBookElementRef,
  showColumnAvailable,
  prices,
  stats = [],
  product_supplier_prices,
  cost,
  rrp_price,
  technorama_price,
  price_change_history,
  active_lt,
  imum_product_tags,
  tag_ids,
  columnWidthMain,
  imum_export_logs,
  failed,
  retail_product_rules,
  added_to_xml,
  received_from_imum,
  stores,
  storeColorMap,
  availableShops = [],
  today_supplier_prices = [],
}) => {
  const isCurrentRange = (from, to) => {
    if (!from || !to) return false;
    const now = dayjs();
    const fromTime = dayjs(from);
    const toTime = dayjs(to);
    return (now.isAfter(fromTime) || now.isSame(fromTime)) &&
      (now.isBefore(toTime) || now.isSame(toTime));
  };

  const getDeptNewPrice = (dept) => {
    const p = prices?.find(
      (pr) => pr.price_type === "new" && pr.department === dept
    );
    const freezeEntry = prices?.find(
      (pr) => pr.price_type === "freeze" && pr.department === dept
    );
    const discountEntry = prices?.find(
      (pr) => pr.price_type === "discount" && pr.department === dept
    );
    const baseRecord = p || freezeEntry || discountEntry;
    if (!baseRecord) return {};

    const freezeEnabled = !!freezeEntry && freezeEntry.active !== false;
    const discountEnabled = !!discountEntry && discountEntry.active !== false;
    const freezeCurrent = freezeEnabled && isCurrentRange(
      freezeEntry?.freeze_from,
      freezeEntry?.freeze_to,
    );
    const discountCurrent = discountEnabled && isCurrentRange(
      discountEntry?.freeze_from,
      discountEntry?.freeze_to,
    );
    const currentPrice = discountCurrent && discountEntry
      ? discountEntry
      : freezeCurrent && freezeEntry
        ? freezeEntry
        : p || freezeEntry || discountEntry;

    const exportSource = p || currentPrice;

    return {
      price: currentPrice?.price ?? baseRecord.price,
      id: currentPrice?.id ?? baseRecord.id,
      newPrice: p?.price ?? null,
      newPriceId: p?.id ?? null,
      freezeRecordPrice: freezeEntry?.price ?? null,
      freezeRecordId: freezeEntry?.id ?? null,
      freezeRecordFrom: freezeEntry?.freeze_from ?? null,
      freezeRecordTo: freezeEntry?.freeze_to ?? null,
      discountRecordPrice: discountEntry?.price ?? null,
      discountRecordId: discountEntry?.id ?? null,
      discountRecordFrom: discountEntry?.freeze_from ?? null,
      discountRecordTo: discountEntry?.freeze_to ?? null,
      discountRecordType: discountEntry?.discount_type ?? "fixed",
      discountRecordAmount: discountEntry?.discount_amount ?? discountEntry?.price ?? null,
      export_date: exportSource?.export_date ?? null,
      export_status: exportSource?.export_status ?? null,
      changed_price: p?.changed_price ?? false,
      retail_product_rule: exportSource?.retail_product_rule_id
        ? (
          retail_product_rules?.find((s) => s.value == exportSource.retail_product_rule_id)?.label ||
          String(exportSource.retail_product_rule_id)
        )
        : null,
      freeze: freezeEnabled,
      freeze_price: freezeEntry?.price ?? null,
      freeze_from: freezeEntry?.freeze_from ?? null,
      freeze_to: freezeEntry?.freeze_to ?? null,
      freeze_current: freezeCurrent,
      discount: discountEnabled,
      discount_price: discountEntry?.price ?? null,
      discount_from: discountEntry?.freeze_from ?? null,
      discount_to: discountEntry?.freeze_to ?? null,
      discount_current: discountCurrent,
    };
  };

  const getDeptOldPrice = (dept) =>
    product_supplier_prices?.find((p) => p.department === dept)?.old_price ??
    null;

  const getDeptStatValue = (dept, statType) => {
    const stat = (stats || []).find(
      (s) => s.department === dept && s.stat_type === statType
    );
    if (!stat) return null;
    return stat.value;
  };

  const inactive = useMemo(() => {
    return !active_lt;
  }, [active_lt]);

  const changed_price = useMemo(() => {
    return prices?.some((d) => d.price_type === "new" && d.changed_price) || false;
  }, [prices]);

  return (
    <tr className="align-middle tableColumn" ref={lastBookElementRef}>
      <TdProductTitle
        product_id={id}
        product_name={product_name}
        changed_price={changed_price}
        checked={checked}
        setChecked={setChecked}
        price_change_history={price_change_history}
        inactive={inactive}
        tag_ids={tag_ids}
        imum_product_tags={imum_product_tags}
        columnWidthMain={columnWidthMain}
      />
      {showColumnAvailable("SKU") && (
        <td className="align-middle">{product_code}</td>
      )}
      {showColumnAvailable("MPN") && (
        <td className="align-middle">{mpn || "-"}</td>
      )}
      {showColumnAvailable("EAN") && (
        <td className="align-middle">{ean_code || "-"}</td>
      )}
      {showColumnAvailable("Prod. cat.") && (
        <td className="align-middle">
          <nobr>{imum_category || "-"}</nobr>
        </td>
      )}
      {showColumnAvailable("Brand") && (
        <td className="align-middle">{brand || "-"}</td>
      )}
      {showColumnAvailable("Prime cost") && (
        <td className="align-middle">
          <nobr>{cost ? returnEuroString(cost) : "-"}</nobr>
        </td>
      )}
      {showColumnAvailable("Technorama.lt") && (
        <td className="align-middle">
          <nobr>{technorama_price ? returnEuroString(technorama_price) : "-"}</nobr>
        </td>
      )}
      {showColumnAvailable("RRP") && (
        <td className="align-middle">
          <RrpCell id={id} rrp_price={rrp_price} />
        </td>
      )}
      {showColumnAvailable("Failure") && (
        <td className="align-middle">
          {imum_export_logs?.length ? (
            <ExportFailures logs={imum_export_logs} />
          ) : (
            "-"
          )}
        </td>
      )}
      <td
        className="align-middle"
        style={{ width: 30 }}
      >
        <div style={{ position: "relative" }}>
          <Tooltip
            placement="top"
            overlay={
              <div>
                <p className="m-0">
                  <span style={{ fontWeight: "600" }}>BB</span>:{" "}
                  {added_to_xml
                    ? dayjs(added_to_xml).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </p>
                <p className="m-0">
                  <span style={{ fontWeight: "600" }}>IMUM</span>:{" "}
                  {received_from_imum
                    ? dayjs(received_from_imum).format("YYYY-MM-DD HH:mm")
                    : "-"}
                </p>
              </div>
            }
          >
            <div style={{ position: "absolute", right: "-20px", top: "-13px" }}>
              {IconImumClock()}
            </div>
          </Tooltip>
        </div>
      </td>
      {showColumnAvailable("Old price") &&
        stores.map((store, idx) => {
          const oldPrice = getDeptOldPrice(store);
          return (
            <RetailStyledTd
              key={`old_${store}_${id}`}
              bgColor={storeColorMap[store]}
              isFirst={idx === 0}
              isLast={idx === stores.length - 1}
            >
              <nobr>{oldPrice ? returnEuroString(oldPrice) : "-"}</nobr>
            </RetailStyledTd>
          );
        })}
      {showColumnAvailable("New price") &&
        stores.map((store, idx) => {
          const deptPrice = getDeptNewPrice(store);
          const oldDeptPrice = getDeptOldPrice(store);
          return (
            <RetailNewPriceChange
              key={`new_${store}_${id}`}
              active={active}
              setActive={setActive}
              store={store}
              storeColor={storeColorMap[store]}
              isFirst={idx === 0}
              isLast={idx === stores.length - 1}
              value={deptPrice.price}
              priceId={deptPrice.id}
              newPrice={deptPrice.newPrice ?? null}
              newPriceId={deptPrice.newPriceId ?? null}
              freezeRecordPrice={deptPrice.freezeRecordPrice ?? null}
              freezeRecordId={deptPrice.freezeRecordId ?? null}
              freezeRecordFrom={deptPrice.freezeRecordFrom ?? null}
              freezeRecordTo={deptPrice.freezeRecordTo ?? null}
              discountRecordPrice={deptPrice.discountRecordPrice ?? null}
              discountRecordId={deptPrice.discountRecordId ?? null}
              discountRecordFrom={deptPrice.discountRecordFrom ?? null}
              discountRecordTo={deptPrice.discountRecordTo ?? null}
              discountRecordType={deptPrice.discountRecordType ?? "fixed"}
              discountRecordAmount={deptPrice.discountRecordAmount ?? null}
              departments={stores}
              prices={prices || []}
              parentId={id}
              exportStatus={deptPrice.export_status}
              exportDate={deptPrice.export_date}
              retailProductRule={deptPrice.retail_product_rule}
              freeze={deptPrice.freeze ?? false}
              freezePrice={deptPrice.freeze_price ?? null}
              freezeFrom={deptPrice.freeze_from ?? null}
              freezeTo={deptPrice.freeze_to ?? null}
              freezeCurrent={deptPrice.freeze_current ?? false}
              discount={deptPrice.discount ?? false}
              discountPrice={deptPrice.discount_price ?? null}
              discountFrom={deptPrice.discount_from ?? null}
              discountTo={deptPrice.discount_to ?? null}
              discountCurrent={deptPrice.discount_current ?? false}
              oldPrice={oldDeptPrice}
              lastHistoryPrice={
                [...(price_change_history || [])]
                  .filter((h) => (h.department || h.country) === store)
                  .at(-1)?.price ?? null
              }
            />
          );
        })}
      {showColumnAvailable("New margin") &&
        stores.map((store, idx) => {
          const margin = getDeptStatValue(store, "margin");
          return (
            <RetailStyledTd
              key={`margin_${store}_${id}`}
              bgColor={storeColorMap[store]}
              isFirst={idx === 0}
              isLast={idx === stores.length - 1}
            >
              <nobr>{margin === null ? "-" : `${numberRound(margin)} %`}</nobr>
            </RetailStyledTd>
          );
        })}
      {showColumnAvailable("Price change") &&
        stores.map((store, idx) => {
          const change = getDeptStatValue(store, "change");
          return (
            <RetailStyledTd
              key={`change_${store}_${id}`}
              bgColor={storeColorMap[store]}
              isFirst={idx === 0}
              isLast={idx === stores.length - 1}
            >
              <nobr className={change && +change < 0 ? "text-danger" : ""}>
                {change === null ? "-" : numberRound(change)}
              </nobr>
            </RetailStyledTd>
          );
        })}
      {showColumnAvailable("Cheapest price") &&
        stores.map((store, idx) => {
          const cheapestPrice = getDeptStatValue(store, "cheapest");
          return (
            <RetailStyledTd
              key={`cheapest_${store}_${id}`}
              bgColor={storeColorMap[store]}
              isFirst={idx === 0}
              isLast={idx === stores.length - 1}
            >
              <nobr>
                {cheapestPrice === null ? "-" : returnEuroString(cheapestPrice)}
              </nobr>
            </RetailStyledTd>
          );
        })}
      {showColumnAvailable("Store prices") &&
        availableShops.map((s) => (
          <td
            key={`shop_${s.country}_${s.shop}_${id}`}
            className="align-middle text-center"
          >
            <FindShopPrice
              shop={s.shop}
              country={s.country}
              data={today_supplier_prices}
            />
          </td>
        ))}
    </tr>
  );
};

const FindShopPrice = ({ shop, country, data }) => {
  const price = useMemo(() => {
    if (!data || data.length === 0) return "-";
    const find_price = data.find(
      (d) => d.shop === shop && d.category === country
    );
    if (!find_price) return "-";
    // kaina24 reports the cheapest seller it found; keep that name reachable.
    const amount = find_price.seller ? (
      <Tooltip placement="top" title={`kaina24: ${find_price.seller}`}>
        <span style={{ borderBottom: "1px dotted #C4C4C4", cursor: "help" }}>
          {returnEuroString(find_price.price)}
        </span>
      </Tooltip>
    ) : (
      returnEuroString(find_price.price)
    );
    if (find_price.active) return amount;
    return (
      <>
        {amount}
        <Tooltip placement="top" title="Out of stock">
          <span
            className="d-flex align-items-center justify-content-center px-1 mr-2"
            style={{
              height: 16,
              background: "rgba(217, 217, 217, 0.4)",
              borderRadius: 30,
              fontWeight: 600,
              fontSize: 9,
              color: "#C4C4C4",
            }}
          >
            OOS
          </span>
        </Tooltip>
      </>
    );
  }, [data, shop, country]);

  return <nobr>{price}</nobr>;
};

export default RetailProductRow;

const RetailStyledTd = styled.td`
  min-width: 100px;
  text-align: center;
  vertical-align: middle !important;
  background-color: ${(props) => props.bgColor || "#fff"};
  border-left: ${(props) =>
    props.isFirst ? "1px solid rgb(191, 191, 191)" : "none"};
  border-right: ${(props) =>
    props.isLast ? "1px solid rgb(191, 191, 191)" : "none"};
`;

const TdProductTitle = ({
  product_id,
  product_name,
  changed_price,
  setChecked,
  checked,
  price_change_history,
  inactive,
  tag_ids,
  imum_product_tags,
  columnWidthMain,
}) => {
  const imum_product_tags_used = (imum_product_tags || []).filter((d) =>
    (tag_ids || []).includes(d.value)
  );

  return (
    <td
      className="align-middle p-left"
      style={{ paddingTop: 0, paddingBottom: 0 }}
    >
      {changed_price && (
        <div className="position-relative">
          <Tooltip placement="left" title="Changed price">
            <div
              className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
              style={{
                left: "-16px",
                top: 34,
                backgroundColor: "#0088BF",
                width: 15,
                height: 15,
                transform: "translate(0px, -50%)",
                zIndex: 10,
              }}
            >
              <AiOutlineExclamation className="text-xs" color="#fff" />
            </div>
          </Tooltip>
        </div>
      )}
      <div style={{ width: columnWidthMain }}>
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ padding: "0.75rem 0.55rem 0.75rem 0" }}
        >
          <div
            className="flex-1"
            style={{ textOverflow: "ellipsis", overflow: "hidden" }}
          >
            <CheckBoxInput
              onChange={setChecked}
              checked={checked}
              text={
                <div className="d-flex align-items-center">
                  {inactive && (
                    <Tooltip placement="top" title="Inactive LT">
                      <span
                        className="d-flex align-items-center justify-content-center mr-2"
                        style={{
                          width: 16,
                          height: 16,
                          background: "rgba(217, 217, 217, 0.4)",
                          borderRadius: 30,
                          fontWeight: 600,
                          fontSize: 9,
                          color: "#C4C4C4",
                        }}
                      >
                        IN
                      </span>
                    </Tooltip>
                  )}
                  {imum_product_tags_used.map((d) => (
                    <Tooltip key={d.value} placement="top" title={d.label}>
                      <div className="mr-1">
                        <BlockSquare color={d.color} />
                      </div>
                    </Tooltip>
                  ))}
                  <div>{product_name}</div>
                </div>
              }
            />
          </div>
          <div>
            {price_change_history.length > 0 && (
              <PriceChangeHistory
                price_change_history={price_change_history}
              />
            )}
            <ImumProductTagPopover
              product_id={product_id}
              tag_ids={tag_ids || []}
              imum_product_tags={imum_product_tags}
              context="retail"
            />
          </div>
        </div>
      </div>
    </td>
  );
};

const PriceChangeHistory = ({ price_change_history }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <Popover
      placement="right"
      title={
        <div className="d-flex align-items-center justify-content-between py-2">
          <div className="font-semi-bold text-base text-black">
            Price change history
          </div>
          <XIcon
            className="cursor-pointer text-black"
            onClick={() => setShowModal(false)}
          />
        </div>
      }
      content={
        showModal ? (
          <div>
            <TableUi
              hoverable={false}
              ths={["Dept.", "Date", "Price", "User"]}
            >
              {price_change_history.map((e, idx) => (
                <TableUi.tr key={`${e.id || 0}_${idx}`}>
                  <TableUi.td className="p-left">
                    {(e.department || e.country || '').toUpperCase()}
                  </TableUi.td>
                  <TableUi.td>{prettyDateFormat(e.created_at)}</TableUi.td>
                  <TableUi.td>{numberRound(e.price)}</TableUi.td>
                  <TableUi.td className="p-right">{e.full_name}</TableUi.td>
                </TableUi.tr>
              ))}
            </TableUi>
          </div>
        ) : (
          ""
        )
      }
      trigger="click"
      visible={showModal || false}
    >
      <div className="mx-1" onClick={() => setShowModal(true)}>
        <IconHistory color="#C4C4C4" />
      </div>
    </Popover>
  );
};
